import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { UserConfig } from '../../config/users/user.interface';
import { OvertimeRecord, OvertimeResolutionPayload } from './overtime.interface';
import { OvertimeResolutionDialogComponent } from './overtime-resolution-dialog.component';

interface ApiListResponse<T> {
  data?: T[];
  items?: T[];
  results?: T[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  name: string;
  children?: OrgUnitRecord[];
}

interface OvertimeTimelineItem {
  title: string;
  actor: string;
  date: string;
  description: string;
  primary: boolean;
}

@Component({
  selector: 'app-overtime-detail',
  standalone: true,
  imports: [CommonModule, BreadcrumbComponent, LoadingStateComponent, MatButtonModule, MatCardModule, MatIconModule, TranslateModule],
  templateUrl: './overtime-detail.component.html',
  styleUrl: './overtime-detail.component.scss',
})
export class OvertimeDetailComponent implements OnInit, AfterViewInit {
  @ViewChild('detailBody') detailBody?: ElementRef<HTMLElement>;

  selectedOvertime: OvertimeRecord | null = null;
  selectedOvertimeId = '';
  isLoading = true;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private companies: CompaniesRecord[] = [];
  private orgUnits: OrgUnitRecord[] = [];

  get detailTitle(): string {
    return this.selectedOvertime?.employeeLabel || this.selectedOvertime?.employeeNumber || this.t('overtimesCatalog.detail.title');
  }

  get detailDescription(): string {
    if (!this.selectedOvertime?.workDateLabel || this.selectedOvertime.workDateLabel === '-') {
      return this.t('overtimesCatalog.detail.description');
    }
    return this.t('overtimesCatalog.detail.descriptionWithDate', { date: this.selectedOvertime.workDateLabel });
  }

  get canResolve(): boolean {
    return this.selectedOvertime?.status === 'pending' && this.hasAccess('approve');
  }

  get canDelete(): boolean {
    return this.hasAccess('delete');
  }

  get employeeInitials(): string {
    const name = this.selectedOvertime?.employeeLabel || this.selectedOvertime?.employeeNumber || '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '--';
    }
    return (parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[1][0]}`).toUpperCase();
  }

  get statusIcon(): string {
    if (this.selectedOvertime?.status === 'approved') {
      return 'check_circle';
    }
    if (this.selectedOvertime?.status === 'rejected') {
      return 'cancel';
    }
    return 'schedule';
  }

  get detectedHoursLabel(): string {
    const minutes = this.selectedOvertime?.detectedMinutes;
    if (minutes === null || minutes === undefined) {
      return '-';
    }
    const value = new Intl.NumberFormat(this.currentLocale, { maximumFractionDigits: 2 }).format(minutes / 60);
    return this.t('overtimesCatalog.detail.hoursValue', { value });
  }

  get workDayLabel(): string {
    const value = this.selectedOvertime?.workDate;
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const weekday = new Intl.DateTimeFormat(this.currentLocale, { weekday: 'long' }).format(date);
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }

  get historyTimeline(): OvertimeTimelineItem[] {
    const overtime = this.selectedOvertime;
    if (!overtime) {
      return [];
    }

    const items: OvertimeTimelineItem[] = [{
      title: this.t('overtimesCatalog.history.created'),
      actor: this.t('overtimesCatalog.labels.system'),
      date: overtime.requestedAtLabel || overtime.createdAtLabel || '-',
      description: this.t('overtimesCatalog.history.createdDescription'),
      primary: true,
    }];

    for (const entry of overtime.history || []) {
      const status = entry.status || overtime.status || 'pending';
      items.push({
        title: this.t(`overtimesCatalog.history.${status}`),
        actor: entry.resolvedByName || entry.resolvedByUid || this.t('overtimesCatalog.labels.system'),
        date: this.formatDateTime(entry.resolvedAt || entry.createdAt),
        description: entry.comment || this.t(`overtimesCatalog.history.${status}Description`),
        primary: false,
      });
    }

    if (!(overtime.history || []).length) {
      const status = overtime.status || 'pending';
      items.push({
        title: this.t(`overtimesCatalog.history.${status}`),
        actor: overtime.resolvedByName || overtime.resolvedByUid || this.t('overtimesCatalog.labels.system'),
        date: overtime.resolvedAtLabel || overtime.requestedAtLabel || '-',
        description: overtime.resolutionComment || this.t(`overtimesCatalog.history.${status}Description`),
        primary: false,
      });
    }

    return items;
  }

  ngOnInit(): void {
    this.selectedOvertimeId = this.route.snapshot.paramMap.get('overtimeId') || '';
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedOvertime) {
        this.selectedOvertime = this.normalizeOvertime(this.selectedOvertime);
      }
    });
    void this.initializeData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.detailBody?.nativeElement.scrollTo({ top: 0 }), 0);
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await Promise.all([this.loadCompanies(), this.loadOrgUnits()]);
      await this.loadDetail();
    } finally {
      this.isLoading = false;
    }
  }

  async loadDetail(): Promise<void> {
    if (!this.selectedOvertimeId) {
      this.selectedOvertime = null;
      return;
    }

    try {
      const response = await firstValueFrom(this.apiService.get<OvertimeRecord>(`overtimes/${this.selectedOvertimeId}`));
      this.selectedOvertime = this.normalizeOvertime(response);
      await this.resolveActorNames();
    } catch (error) {
      console.error('Error loading overtime detail', error);
      this.selectedOvertime = null;
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.loadDetailError')), 'bottom', 'center');
    }
  }

  async goBackToList(): Promise<void> {
    await this.router.navigate(['/attendance/overtimes'], { queryParamsHandling: 'preserve' });
  }

  async approveSelected(): Promise<void> {
    await this.resolveSelected('approve');
  }

  async rejectSelected(): Promise<void> {
    await this.resolveSelected('reject');
  }

  async deleteSelected(): Promise<void> {
    if (!this.selectedOvertime || !this.hasAccess('delete')) {
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog.open(ConfirmDeleteDialogComponent, {
        width: '420px',
        data: {
          title: this.t('overtimesCatalog.dialog.deleteTitle'),
          message: this.t('overtimesCatalog.dialog.deleteMessage', { count: 1 }),
          confirmText: this.t('overtimesCatalog.dialog.deleteConfirm'),
        },
      }).afterClosed()
    );

    if (!confirmed) {
      return;
    }

    try {
      await firstValueFrom(this.apiService.delete(`overtimes/${this.selectedOvertime.overtimeId}`));
      this.showNotification('snackbar-success', this.t('overtimesCatalog.notifications.deleted'), 'bottom', 'center');
      await this.goBackToList();
    } catch (error) {
      console.error('Error deleting overtime', error);
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.deleteError')), 'bottom', 'center');
    }
  }

  private async resolveSelected(mode: 'approve' | 'reject'): Promise<void> {
    if (!this.selectedOvertime || !this.canResolve) {
      return;
    }

    const payload = await firstValueFrom(
      this.dialog.open(OvertimeResolutionDialogComponent, {
        width: '460px',
        data: { mode, detectedMinutes: this.selectedOvertime.detectedMinutes },
      }).afterClosed()
    );

    if (!payload) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.post<OvertimeRecord>(`overtimes/${this.selectedOvertime.overtimeId}/${mode}`, payload as OvertimeResolutionPayload)
      );
      this.selectedOvertime = this.normalizeOvertime(response || this.selectedOvertime);
      const notificationKey = mode === 'approve' ? 'approved' : 'rejected';
      this.showNotification('snackbar-success', this.t(`overtimesCatalog.notifications.${notificationKey}`), 'bottom', 'center');
      await this.loadDetail();
    } catch (error) {
      console.error(`Error resolving overtime ${mode}`, error);
      this.showNotification('snackbar-danger', extractApiErrorMessage(error, this.t('overtimesCatalog.notifications.resolveError')), 'bottom', 'center');
    }
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | ApiListResponse<CompaniesRecord>>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractList(response);
    } catch (error) {
      console.error('Error loading companies for overtime detail', error);
      this.companies = [];
    }
  }

  private async loadOrgUnits(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<OrgUnitRecord[] | ApiListResponse<OrgUnitRecord>>('org-units', {
          params: { page: 1, perPage: 200, isActive: 'true' },
        })
      );
      this.orgUnits = this.flattenOrgUnits(this.extractList(response));
    } catch (error) {
      console.error('Error loading org units for overtime detail', error);
      this.orgUnits = [];
    }
  }

  private normalizeOvertime(item: Partial<OvertimeRecord>): OvertimeRecord {
    const status = item.status || 'pending';
    const employeeLabel = item.employeeName || item.employeeNumber || item.employeeId || '-';

    return {
      overtimeId: item.overtimeId || this.selectedOvertimeId,
      center: item.center || '',
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || '-',
      employeeName: item.employeeName || null,
      companiesRecordId: item.companiesRecordId || null,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      workDate: item.workDate || null,
      attendanceResultId: item.attendanceResultId || null,
      detectedMinutes: item.detectedMinutes ?? null,
      approvedMinutes: item.approvedMinutes ?? null,
      status,
      requestedByUid: item.requestedByUid || null,
      requestedByName: item.requestedByName || null,
      requestedAt: item.requestedAt || null,
      resolvedByUid: item.resolvedByUid || null,
      resolvedByName: item.resolvedByName || null,
      resolvedAt: item.resolvedAt || null,
      resolutionComment: item.resolutionComment || null,
      history: item.history || [],
      isActive: item.isActive !== false,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeLabel,
      companyName: this.getCompanyName(item.companiesRecordId),
      orgUnitName: this.getOrgUnitName(item.orgUnitId),
      workDateLabel: this.formatDate(item.workDate),
      detectedMinutesLabel: this.formatMinutes(item.detectedMinutes),
      approvedMinutesLabel: this.formatMinutes(item.approvedMinutes),
      statusLabel: this.translateStatus(status),
      requestedAtLabel: this.formatDateTime(item.requestedAt),
      resolvedAtLabel: this.formatDateTime(item.resolvedAt),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
      activeLabel: item.isActive !== false ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private extractList<T>(response: T[] | ApiListResponse<T>): T[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || [];
  }

  private async resolveActorNames(): Promise<void> {
    const overtime = this.selectedOvertime;
    if (!overtime) {
      return;
    }

    const actorUids = [
      overtime.requestedByUid,
      overtime.resolvedByUid,
      ...(overtime.history || []).map((entry) => entry.resolvedByUid),
    ].filter((uid): uid is string => !!uid);

    if (!actorUids.length) {
      return;
    }

    try {
      const users = await firstValueFrom(this.apiService.get<UserConfig[]>('users'));
      const userName = (uid?: string | null): string | null => {
        if (!uid) {
          return null;
        }
        const user = (users || []).find((item) => item.uid === uid);
        return user?.displayName || user?.email || uid;
      };

      this.selectedOvertime = {
        ...overtime,
        requestedByName: userName(overtime.requestedByUid),
        resolvedByName: userName(overtime.resolvedByUid),
        history: (overtime.history || []).map((entry) => ({
          ...entry,
          resolvedByName: userName(entry.resolvedByUid),
        })),
      };
    } catch (error) {
      console.warn('Could not resolve overtime actors', error);
    }
  }

  private flattenOrgUnits(units: OrgUnitRecord[]): OrgUnitRecord[] {
    return units.flatMap((unit) => [unit, ...this.flattenOrgUnits(unit.children || [])]);
  }

  private getCompanyName(id?: string | null): string {
    if (!id) {
      return this.t('overtimesCatalog.labels.noCompany');
    }
    return this.companies.find((company) => company.companiesRecordId === id)?.name || id;
  }

  private getOrgUnitName(id?: string | null): string {
    if (!id) {
      return this.t('overtimesCatalog.labels.noOrgUnit');
    }
    return this.orgUnits.find((unit) => unit.orgUnitId === id)?.name || id;
  }

  private translateStatus(status?: string | null): string {
    if (!status) {
      return '-';
    }
    const key = `overtimesCatalog.statuses.${status}`;
    const translated = this.t(key);
    return translated !== key ? translated : status;
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(this.currentLocale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(this.currentLocale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
  }

  private get currentLocale(): string {
    const language = this.translate.currentLang || this.translate.defaultLang || 'es';
    if (language.startsWith('de')) {
      return 'de-DE';
    }
    return language.startsWith('en') || language.startsWith('us') ? 'en-US' : 'es-MX';
  }

  private formatMinutes(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    if (!hours) {
      return `${minutes}m`;
    }
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'overtimes', action });
  }

  private showNotification(colorName: string, text: string, placementFrom: MatSnackBarVerticalPosition, placementAlign: MatSnackBarHorizontalPosition): void {
    this.snackBar.open(text, '', { duration: 2200, verticalPosition: placementFrom, horizontalPosition: placementAlign, panelClass: colorName });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
